import React, { useCallback, useMemo } from 'react'
import { ApolloError } from 'apollo-client'
import {
  type AccountFormProps,
  type RetailerTerminology,
} from 'domains/account-management/components/account-form/utils/AccountForm.types'
import {
  AccountManagementAccountForm,
  emptyInitialState,
} from 'domains/account-management/components/account-form/AccountManagementAccountForm'
import { useCreateEnterpriseAccount } from 'domains/account-management/api/enterprise-account/create/useCreateEnterpriseAccount.hooks'
import {
  roleToOption,
  toCreatePayload,
} from 'domains/account-management/components/account-form/utils/AccountForm.utils'
import { trackAccountCreated } from 'domains/account-management/utils/AccountManagement.tracking'
import { useCurrentAccountDetails } from 'api/current-account/currentAccount.hooks'
import {
  SuccessNotification,
  ErrorNotification,
} from 'domains/account-management/components/account-create/components/notifications/AccountFormNotifications'
import { useIsReadOnly } from 'gin-and-tonic/utils/access/useIsReadOnly'
import { useRoleById } from 'domains/account-management/utils/useRoleById.hooks'

interface Props {
  showAllPartnersFullAccessToggle?: boolean
  retailerTerminology: RetailerTerminology
  partnerId?: string
  preselectRoleId?: string
}

export const AccountManagementCreateAccount: React.FunctionComponent<Props> = ({
  showAllPartnersFullAccessToggle,
  retailerTerminology,
  partnerId,
  preselectRoleId,
}) => {
  const { submit, apiLoading } = useCreateEnterpriseAccount()
  const { loading: currentAccountLoading, account: currentAccount } = useCurrentAccountDetails()

  const { role, loading: roleLoading } = useRoleById(preselectRoleId)
  const roleOptions = useMemo(() => {
    if (!role) {
      return []
    }

    return [roleToOption(role)]
  }, [role])

  const onFormSubmit: AccountFormProps['onFormSubmit'] = useCallback(
    async (formData, form) => {
      await submit(toCreatePayload(formData)).then(result => {
        if (!result.errors) {
          trackAccountCreated({ formData })
          return
        }

        throw new ApolloError({
          errorMessage: 'There was an error creating the account',
          graphQLErrors: result.errors,
        })
      })

      if (formData.createAnother) {
        // reset the form with the existing store selection data
        form.reset({
          ...emptyInitialState,
          hasAllPartnersFullAccess: formData.hasAllPartnersFullAccess,
          hasFullPartnerAccess: formData.hasFullPartnerAccess,
          hasFullRetailerAccess: formData.hasFullRetailerAccess,
          retailers: formData.retailers,
          partners: formData.partners,
          retailerLocations: formData.retailerLocations,
          retailerLocationsStoreGroups: formData.retailerLocationsStoreGroups,
          createAnother: true,
        })
      } else {
        form.reset(emptyInitialState)
      }
    },
    [submit]
  )

  const isReadOnly = useIsReadOnly()

  if (currentAccountLoading) return null
  if (roleLoading) return null

  const {
    hasAllPartnersFullAccess: allowAllPartnerFullAccess,
    hasFullPartnerAccess: allowFullPartnerAccess,
    hasFullRetailerAccess: allowFullRetailerAccess,
  } = currentAccount.internalAccessConfiguration

  return (
    <AccountManagementAccountForm
      showAllPartnersFullAccessToggle={showAllPartnersFullAccessToggle}
      retailerTerminology={retailerTerminology}
      allowAllPartnersFullAccess={allowAllPartnerFullAccess}
      allowFullPartnerAccess={allowFullPartnerAccess}
      allowFullRetailerAccess={allowFullRetailerAccess}
      onFormSubmit={onFormSubmit}
      formLoading={apiLoading || currentAccountLoading}
      SuccessNotification={SuccessNotification}
      ErrorNotification={ErrorNotification}
      partnerId={partnerId}
      allowCreateAnother={true}
      isUpdate={false}
      isCurrentAccountReadOnly={isReadOnly}
      initialState={{ ...emptyInitialState, roles: roleOptions }}
    />
  )
}
